import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, X, ArrowUpRight } from "lucide-react";
import { LOGO_PRIMARY, LOGO_WHITE } from "../lib/media";
import { ORDER_ONLINE_URL } from "../lib/links";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: ORDER_ONLINE_URL, label: "Order Online", external: true },
  { to: "/offers", label: "Offers" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/visit", label: "Visit Us" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-[#D1C9BE]/60"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 h-24 md:h-28 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="SugaSpice home">
            <img
              src={LOGO_PRIMARY}
              alt="SugaSpice Bakery & Kitchen logo"
              width="256"
              height="256"
              fetchpriority="high"
              decoding="async"
              className="h-20 md:h-24 w-auto select-none"
              draggable="false"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.filter((n) => !["Home"].includes(n.label)).map((n) => (
              n.external ? (
                <a
                  key={n.to}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors text-[#1A1A1A] hover:text-[#C8102E]"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors ${
                      isActive ? "text-[#C8102E]" : "text-[#1A1A1A] hover:text-[#C8102E]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={ORDER_ONLINE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-order-cta"
              className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]"
            >
              Order Online <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-[#1A1A1A]/15 bg-[#F7F5F0]"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Mobile menu — rendered into <body> via portal so it sits on top of EVERY other layer
          (header, sticky CTAs, hero overlays, etc.) on every page. */}
      {open && createPortal(
        <div
          className="fixed inset-0 text-[#F7F5F0] overflow-y-auto"
          style={{ backgroundColor: "#1A1A1A", zIndex: 9999 }}
          data-testid="mobile-menu-panel"
        >
          <div className="px-6 h-24 flex items-center justify-between border-b border-white/10">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3">
              <img loading="lazy" decoding="async" src={LOGO_WHITE} alt="SugaSpice" className="h-16 w-auto" />
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-[#F7F5F0]"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="px-8 pt-8 pb-12 flex flex-col gap-5">
            {NAV.map((n) => (
              n.external ? (
                <a
                  key={n.to}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-4xl tracking-tight text-[#F7F5F0]"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-4xl tracking-tight ${isActive ? "text-[#C8102E]" : "text-[#F7F5F0]"}`
                  }
                >
                  {n.label}
                </NavLink>
              )
            ))}
            <a
              href={ORDER_ONLINE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="btn-pill mt-4 bg-[#C8102E] text-[#F7F5F0] w-fit"
              data-testid="mobile-order-cta"
            >
              Order Online <ArrowUpRight className="h-4 w-4" />
            </a>
          </nav>
        </div>,
        document.body
      )}
    </>
  );
}
